import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, Pause, Headphones, Heart, Brain, Moon, Shield, Leaf, Users, Target, Compass, Smartphone, RefreshCw, Mail, Check, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ProductCard } from '../components/ProductCard';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WELCOME_AUDIO = "https://customer-assets.emergentagent.com/job_hypno-academy-1/artifacts/uv0s27wq_Eric%20Paris%20%E2%80%93%20Welcome%20to%20the%20Hypnosis%20Mastery%20Series.mp3";

const ERIC_PHOTO = "https://customer-assets.emergentagent.com/job_3d9cf4ee-16b6-4dd1-aa5d-11b4c5ff8dd4/artifacts/yoqwutsk_Eric%20Paris.webp";

const categories = [
    { name: 'Volume I – Emotional Freedom', icon: Heart, description: 'Release anxiety, stress, overthinking, negativity and trauma', helpText: '"I feel anxious or overwhelmed"' },
    { name: 'Volume II – Inner Confidence', icon: Shield, description: 'Overcome insecurity, self-doubt and fear of public speaking', helpText: '"I doubt myself or feel insecure"' },
    { name: 'Volume III – Healthy Habits & Body', icon: Leaf, description: 'Break free from addiction, smoking, emotional eating and procrastination', helpText: '"I want to break a bad habit"' },
    { name: 'Volume IV – Mind-Body Renewal', icon: Moon, description: 'Restore sleep, relieve chronic pain, heal burnout and release phobias', helpText: '"I\'m struggling with sleep"' },
    { name: 'Volume V – Relationships & Connection', icon: Users, description: 'Heal heartbreak, release guilt, jealousy and loneliness', helpText: '"I\'m hurting from a relationship"' },
    { name: 'Volume VI – Focus & Performance', icon: Target, description: 'Sharpen focus, overcome perfectionism and unlock creativity', helpText: '"I can\'t focus or feel stuck"' },
    { name: 'Volume VII – Purpose & Fulfilment', icon: Compass, description: 'Stop self-sabotage, embrace change and resolve inner conflict', helpText: '"I keep getting in my own way"' },
    { name: 'Volume VIII – Digital Freedom & Modern Calm', icon: Smartphone, description: 'Break social media and phone addiction, overcome digital anxiety', helpText: '"I\'m glued to my phone"' },
    { name: 'Volume IX – Digital Detox & Inner Freedom', icon: RefreshCw, description: 'Freedom from vaping, online gambling, porn and doomscrolling', helpText: '"I\'m struggling with an addiction"' },
];

export const Home = () => {
    const [featuredRecordings, setFeaturedRecordings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isWelcomePlaying, setIsWelcomePlaying] = useState(false);
    const [welcomeProgress, setWelcomeProgress] = useState(0);
    const welcomeAudioRef = useRef(null);

    useEffect(() => {
        const fetchRecordings = async () => {
            try {
                const response = await axios.get(`${API}/recordings`);
                const allRecordings = response.data;
                
                // Get one recording from each volume for variety
                const volumeOrder = [
                    'Volume I',
                    'Volume II', 
                    'Volume III',
                    'Volume IV',
                    'Volume V',
                    'Volume VI'
                ];
                
                const featured = [];
                for (const volume of volumeOrder) {
                    const recording = allRecordings.find(r => 
                        r.category?.startsWith(volume) && !featured.includes(r)
                    );
                    if (recording) {
                        featured.push(recording);
                    }
                }
                
                setFeaturedRecordings(featured);
            } catch (error) {
                console.error('Failed to fetch recordings:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchRecordings();
    }, []);

    const toggleWelcomeAudio = () => {
        if (welcomeAudioRef.current) {
            if (isWelcomePlaying) {
                welcomeAudioRef.current.pause();
            } else {
                welcomeAudioRef.current.play();
            }
            setIsWelcomePlaying(!isWelcomePlaying);
        }
    };

    const handleWelcomeTimeUpdate = () => {
        if (welcomeAudioRef.current) {
            const progress = (welcomeAudioRef.current.currentTime / welcomeAudioRef.current.duration) * 100;
            setWelcomeProgress(progress || 0);
        }
    };

    const handleWelcomeEnded = () => {
        setIsWelcomePlaying(false);
        setWelcomeProgress(0);
    };

    return (
        <div className="min-h-screen">
            <SEO 
                title="Hypnosis Mastery Series | Single hypnosis sessions for a mind that won't switch off"
                description="Guided hypnosis recordings by Eric Paris — designed to release stress, dissolve limiting patterns, and restore calm confidence and emotional balance."
                canonical="https://hypnosismasteryseries.com/"
            />

            {/* Hero Section */}
            <section className="bg-nature-800 pt-32 pb-24 sm:pt-40 sm:pb-32">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    {/* Eyebrow */}
                    <p className="text-nature-50/70 font-body text-sm tracking-wide mb-8 opacity-0 animate-fade-in-up">
                        45 sessions · 35 minutes each · yours to keep
                    </p>
                    
                    {/* Headline */}
                    <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-nature-50 leading-tight mb-10 opacity-0 animate-fade-in-up stagger-1">
                        You have probably already tried<br className="hidden sm:block" /> talking yourself out of it.
                    </h1>
                    
                    {/* Subheading */}
                    <p className="text-base sm:text-lg text-nature-50/80 font-body leading-relaxed max-w-2xl mx-auto mb-12 opacity-0 animate-fade-in-up stagger-2">
                        Guided hypnosis recordings for anxiety, sleep, chronic pain, IBS, and the habits that have not shifted. They work on the part of your mind that does not respond to argument.
                    </p>
                    
                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 opacity-0 animate-fade-in-up stagger-3">
                        <Link to="/recording/freedom-from-insomnia" data-testid="hero-free-session-btn">
                            <Button 
                                size="lg" 
                                className="rounded-full bg-copper-500 hover:bg-copper-600 text-white px-8 h-14 text-base"
                            >
                                Start with a free session
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </Button>
                        </Link>
                        <Link 
                            to="/recordings" 
                            className="text-nature-50/80 hover:text-nature-50 font-body text-base transition-colors"
                            data-testid="hero-browse-link"
                        >
                            Browse all 45 →
                        </Link>
                    </div>
                </div>
            </section>

            {/* Welcome from Eric */}
            <section className="py-16 bg-nature-100">
                <div className="max-w-4xl mx-auto px-6">
                    <div className="bg-white rounded-3xl p-8 md:p-10 shadow-soft flex flex-col md:flex-row items-center gap-8">
                        <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden flex-shrink-0 ring-4 ring-copper-500/20">
                            <img 
                                src={ERIC_PHOTO} 
                                alt="Eric Paris" 
                                className="w-full h-full object-cover"
                            />
                        </div>
                        
                        <div className="flex-1 text-center md:text-left">
                            <h3 className="font-heading text-2xl font-light text-nature-900 mb-2">
                                Welcome from <span className="italic">Eric Paris</span>
                            </h3>
                            <p className="text-nature-800/70 font-body mb-5">
                                Hear a personal message from Eric about the Hypnosis Mastery Series and how these sessions can support your transformation.
                            </p>
                            
                            <audio 
                                ref={welcomeAudioRef}
                                src={WELCOME_AUDIO}
                                onTimeUpdate={handleWelcomeTimeUpdate}
                                onEnded={handleWelcomeEnded}
                                preload="metadata"
                            />
                            
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={toggleWelcomeAudio}
                                    className="w-14 h-14 rounded-full bg-copper-500 hover:bg-copper-600 text-white flex items-center justify-center transition-colors flex-shrink-0"
                                    data-testid="welcome-play-btn"
                                >
                                    {isWelcomePlaying ? (
                                        <Pause className="w-6 h-6" />
                                    ) : (
                                        <Play className="w-6 h-6 ml-1" />
                                    )}
                                </button>
                                
                                <div className="flex-1">
                                    <div className="h-2 bg-nature-100 rounded-full overflow-hidden">
                                        <div 
                                            className="h-full bg-copper-500 transition-all duration-200"
                                            style={{ width: `${welcomeProgress}%` }}
                                        />
                                    </div>
                                    <p className="text-xs text-nature-800/50 font-body mt-2">
                                        {isWelcomePlaying ? 'Now playing...' : 'Click to listen'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="font-heading text-3xl sm:text-4xl font-light text-nature-900 mb-4">
                            How These Sessions <span className="italic">Work</span>
                        </h2>
                        <p className="text-nature-800/70 font-body max-w-2xl mx-auto">
                            Your brain is designed to learn quickly — especially when it feels calm and supported.
                        </p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { step: '01', title: 'Download', description: 'Purchase and instantly download high-quality MP3 files to keep forever.' },
                            { step: '02', title: 'Listen', description: 'Listen as often as you like, at your own pace, whenever you need support.' },
                            { step: '03', title: 'Transform', description: 'With repetition, new patterns strengthen and change becomes natural.' },
                        ].map((item, index) => (
                            <div 
                                key={item.step}
                                className="text-center p-8 rounded-2xl bg-nature-50 opacity-0 animate-fade-in-up"
                                style={{ animationDelay: `${index * 0.1}s` }}
                            >
                                <span className="inline-block text-5xl font-heading font-light text-copper-500/30 mb-4">
                                    {item.step}
                                </span>
                                <h3 className="font-heading text-xl font-semibold text-nature-900 mb-3">
                                    {item.title}
                                </h3>
                                <p className="text-nature-800/70 font-body text-sm">
                                    {item.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why a Recording */}
            <section className="py-24 bg-nature-800">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <Headphones className="w-12 h-12 text-copper-500 mx-auto mb-8" />
                    <h2 className="font-heading text-2xl sm:text-3xl font-light text-nature-50 mb-6">
                        Why a recording
                    </h2>
                    <p className="font-body text-lg sm:text-xl text-nature-50/90 leading-relaxed mb-8">
                        Listening at home is not the compromise version. In clinical research, hypnosis is frequently delivered exactly this way. The Stanford brain-imaging study used pre-recorded instructions. The 2025 trial behind the Menopause Society&apos;s recommendation for hot flashes tested self-administered audio. For IBS, a 2024 review proposes recordings as the first line of treatment.
                    </p>
                    <Link 
                        to="/blog/does-self-hypnosis-work-at-home-what-the-research-actually-tested"
                        className="inline-flex items-center text-copper-500 font-body font-medium hover:text-copper-400 transition-colors"
                    >
                        What the research actually tested →
                    </Link>
                </div>
            </section>

            {/* Categories */}
            <section className="py-24 bg-nature-50">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="font-heading text-3xl sm:text-4xl font-light text-nature-900 mb-4">
                            Explore <span className="italic">Categories</span>
                        </h2>
                        <p className="text-nature-800/60 font-body">
                            Nine progressive volumes, each with 4–6 sessions.
                        </p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {categories.map((cat, index) => (
                            <Link 
                                key={cat.name}
                                to={`/recordings?category=${encodeURIComponent(cat.name)}`}
                                className="group p-8 rounded-2xl bg-white shadow-soft hover:shadow-hover transition-all opacity-0 animate-fade-in-up"
                                style={{ animationDelay: `${index * 0.1}s` }}
                                data-testid={`category-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                                <p className="text-copper-600 font-body text-sm italic mb-4">
                                    {cat.helpText}
                                </p>
                                <div className="w-14 h-14 rounded-full bg-copper-500/10 flex items-center justify-center mb-4 group-hover:bg-copper-500 transition-colors">
                                    <cat.icon className="w-7 h-7 text-copper-500 group-hover:text-white transition-colors" />
                                </div>
                                <h3 className="font-heading text-xl font-semibold text-nature-900 mb-2 group-hover:text-copper-500 transition-colors">
                                    {cat.name}
                                </h3>
                                <p className="text-nature-800/70 font-body text-sm">
                                    {cat.description}
                                </p>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Quote Section */}
            <section className="py-24 bg-nature-800">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <Headphones className="w-12 h-12 text-copper-500 mx-auto mb-8" />
                    <blockquote className="font-heading text-2xl sm:text-3xl font-light text-nature-50 leading-relaxed mb-8">
                        "Real change doesn't require force. It begins with calm attention and repetition. 
                        Your mind already knows how to learn, adapt, and heal. 
                        These sessions simply guide that process."
                    </blockquote>
                    <p className="text-copper-500 font-body font-medium">
                        — Eric Paris
                    </p>
                </div>
            </section>

            {/* Featured Recordings */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex items-center justify-between mb-12">
                        <div>
                            <h2 className="font-heading text-3xl sm:text-4xl font-light text-nature-900 mb-2">
                                Featured <span className="italic">Recordings</span>
                            </h2>
                            <p className="text-nature-800/70 font-body">
                                Start your transformation journey
                            </p>
                        </div>
                        <Link 
                            to="/recordings" 
                            className="hidden sm:flex items-center gap-2 text-copper-500 hover:text-copper-600 font-body font-medium transition-colors"
                            data-testid="view-all-recordings"
                            aria-label="View all 45 hypnosis recordings"
                        >
                            View All Recordings
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    
                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className="bg-nature-100 rounded-2xl aspect-[4/5] skeleton" />
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {featuredRecordings.map((recording, index) => (
                                <ProductCard key={recording.id} recording={recording} index={index} />
                            ))}
                        </div>
                    )}
                    
                    <div className="text-center mt-12 sm:hidden">
                        <Link to="/recordings" data-testid="view-all-recordings-mobile" aria-label="Browse all 45 hypnosis recordings">
                            <Button variant="outline" className="rounded-full">
                                Browse All 45 Recordings
                                <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* What Listening Is Actually Like */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="font-heading text-3xl sm:text-4xl font-light text-nature-900">
                            What listening is <span className="italic">actually like</span>
                        </h2>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-nature-50 rounded-2xl p-8 opacity-0 animate-fade-in-up">
                            <p className="text-nature-800/80 font-body leading-relaxed">
                                Most people expect something dramatic. The experience is usually quite ordinary — a settled body, a quieter mind, and mild surprise that thirty-five minutes went by.
                            </p>
                        </div>
                        
                        <div 
                            className="bg-nature-50 rounded-2xl p-8 opacity-0 animate-fade-in-up"
                            style={{ animationDelay: '0.1s' }}
                        >
                            <p className="text-nature-800/80 font-body leading-relaxed">
                                Changes tend to show up afterwards. Not during the session, but a day or two later, as a smaller reaction to something that used to be automatic.
                            </p>
                        </div>
                        
                        <div 
                            className="bg-nature-50 rounded-2xl p-8 opacity-0 animate-fade-in-up"
                            style={{ animationDelay: '0.2s' }}
                        >
                            <p className="text-nature-800/80 font-body leading-relaxed">
                                Repetition matters more than intensity. Listening most days for two or three weeks does more than one determined attempt.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 bg-nature-50">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <h2 className="font-heading text-3xl sm:text-4xl font-light text-nature-900 mb-6">
                        Change is <span className="italic">Possible</span>
                    </h2>
                    <p className="text-nature-800/70 font-body text-lg mb-8 max-w-2xl mx-auto">
                        Take back control over your life. Download the sessions and start your journey to transformation today.
                    </p>
                    <Link to="/recordings" data-testid="cta-download-btn">
                        <Button 
                            size="lg" 
                            className="rounded-full bg-copper-500 hover:bg-copper-600 text-white px-10"
                        >
                            Download the MP3 Recordings
                            <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                    </Link>
                    <p className="mt-6 text-nature-800/60 font-body text-sm">
                        Prefer to understand how it works first?{' '}
                        <Link to="/blog/the-science-behind-hypnotherapy" className="text-copper-600 hover:text-copper-700 underline">
                            Learn the science behind hypnotherapy
                        </Link>
                    </p>
                </div>
            </section>

            {/* Newsletter Signup */}
            <NewsletterSection />
        </div>
    );
};

const NewsletterSection = () => {
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) return;

        setIsSubmitting(true);
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, { email });
            setIsSubscribed(true);
            setEmail('');
            toast.success('Welcome! You\'ll be the first to know about new recordings.');
        } catch (error) {
            toast.error('Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="py-20 bg-nature-800">
            <div className="max-w-2xl mx-auto px-6 text-center">
                <Mail className="w-10 h-10 text-copper-500 mx-auto mb-6" />
                <h2 className="font-heading text-2xl sm:text-3xl font-light text-nature-50 mb-4">
                    Stay <span className="italic">Updated</span>
                </h2>
                <p className="text-nature-100/70 font-body mb-8">
                    Be the first to know when new recordings are released. No spam, just transformation.
                </p>
                
                {isSubscribed ? (
                    <div className="flex items-center justify-center gap-2 text-copper-500">
                        <Check className="w-5 h-5" />
                        <span className="font-body">You're subscribed! Thank you.</span>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                        <Input
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="flex-1 rounded-full bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-copper-500"
                            required
                            data-testid="newsletter-email"
                        />
                        <Button 
                            type="submit"
                            disabled={isSubmitting}
                            className="rounded-full bg-copper-500 hover:bg-copper-600 text-white px-8"
                            data-testid="newsletter-submit"
                        >
                            {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                        </Button>
                    </form>
                )}
            </div>
        </section>
    );
};

export default Home;
